import { useState } from "react";
import { Mail, MapPin, MessageCircle, MessageSquare, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { PublicShell } from "./LegalPage";
import { useSiteContent } from "../../lib/site";

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
    const [sending, setSending] = useState(false);
    const site = useSiteContent();

    const submit = async () => {
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            return toast.error("Ad, e-posta ve mesaj alanları zorunludur");
        }
        setSending(true);
        try {
            await api.post("/contact", form);
            toast.success("Mesajınız alındı — en kısa sürede dönüş yapacağız");
            setForm({ name: "", email: "", subject: "", message: "" });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Mesaj gönderilemedi");
        } finally {
            setSending(false);
        }
    };

    return (
        <PublicShell title="İletişim" subtitle="Sorularınız için bize ulaşın" testId="contact-page">
            <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-12">
                <div className="space-y-4 lg:col-span-5">
                    {(() => {
                        const waRaw = (site.site_whatsapp || "").replace(/\D/g, "");
                        const waDisplay = waRaw.length === 12 && waRaw.startsWith("90") ? `+90 ${waRaw.slice(2, 5)} ${waRaw.slice(5, 8)} ${waRaw.slice(8, 10)} ${waRaw.slice(10)}` : waRaw;
                        return [
                            { icon: MapPin, label: "Adres", value: site.contact_address || "Çankaya / Ankara, Türkiye", tid: "contact-address" },
                            { icon: Phone, label: "Telefon", value: site.contact_phone || "", tid: "contact-phone" },
                            { icon: Mail, label: "E-posta", value: site.contact_email || "ataangora@gmail.com", tid: "contact-email" },
                            { icon: MessageCircle, label: "WhatsApp", value: waDisplay, tid: "contact-whatsapp", href: waRaw ? `https://wa.me/${waRaw}?text=${encodeURIComponent("Merhaba, ATA Angora Tedarik Platformu hakkında bilgi almak istiyorum.")}` : null },
                        ].filter((c) => c.value).map((c) => {
                            const inner = (
                                <>
                                    <c.icon className="mt-0.5 h-5 w-5 shrink-0 text-terra-600" />
                                    <div>
                                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{c.label}</p>
                                        <p className="mt-0.5 text-sm font-semibold">{c.value}</p>
                                    </div>
                                </>
                            );
                            const cls = "flex items-start gap-3 rounded-2xl border bg-card p-5 shadow-sm";
                            return c.href ? (
                                <a key={c.tid} href={c.href} target="_blank" rel="noopener noreferrer" className={`${cls} transition-colors hover:border-terra-500/50`} data-testid={c.tid}>{inner}</a>
                            ) : (
                                <div key={c.tid} className={cls} data-testid={c.tid}>{inner}</div>
                            );
                        });
                    })()}
                    <p className="rounded-xl bg-muted p-4 text-xs leading-relaxed text-muted-foreground">
                        Platform içi destek için giriş yaptıktan sonra tedarikçilerinizle doğrudan mesajlaşabilir veya bildirimler üzerinden bilgilendirme alabilirsiniz.
                    </p>
                </div>
                <div className="rounded-2xl border bg-card p-6 shadow-sm lg:col-span-7" data-testid="contact-form-card">
                    <div className="flex items-center gap-2">
                        <MessageSquare className="h-5 w-5 text-terra-600" />
                        <h2 className="font-display text-lg font-bold">Mesaj Gönderin</h2>
                    </div>
                    <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Input placeholder="Ad Soyad / Firma *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
                        <Input type="email" placeholder="E-posta *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" />
                        <div className="sm:col-span-2">
                            <Input placeholder="Konu" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject" />
                        </div>
                        <div className="sm:col-span-2">
                            <Textarea rows={5} placeholder="Mesajınız *" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
                        </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                        <Button onClick={submit} disabled={sending} className="bg-terra-600 hover:bg-terra-700 text-white" data-testid="contact-submit">
                            <Send className="mr-2 h-4 w-4" /> {sending ? "Gönderiliyor..." : "Gönder"}
                        </Button>
                    </div>
                </div>
                <div className="overflow-hidden rounded-2xl border shadow-sm lg:col-span-12" data-testid="contact-map-card">
                    <iframe
                        title="ATA Angora Konum Haritası"
                        src={`https://www.google.com/maps?q=${encodeURIComponent(site.contact_address || "Çankaya / Ankara, Türkiye")}&output=embed&hl=tr`}
                        className="h-72 w-full sm:h-96"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        allowFullScreen
                        data-testid="contact-map"
                    />
                    <div className="flex flex-wrap items-center justify-between gap-3 border-t bg-card px-5 py-3">
                        <p className="text-sm text-muted-foreground" data-testid="contact-map-address">{site.contact_address || "Çankaya / Ankara, Türkiye"}</p>
                        <a
                            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(site.contact_address || "Çankaya / Ankara, Türkiye")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex h-9 items-center gap-2 rounded-lg bg-terra-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-terra-700"
                            data-testid="contact-directions-button"
                        >
                            <MapPin className="h-4 w-4" /> Yol Tarifi Al
                        </a>
                    </div>
                </div>
            </div>
        </PublicShell>
    );
}
